import { useState, useEffect } from 'react';
import { Package, Clock, ShieldCheck, AlertTriangle, CheckCircle, XCircle, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';

const STATUS_CONFIG = {
  pending:    { color: '#F5A623', icon: Clock,        label: 'Pending' },
  confirmed:  { color: '#4DA2FF', icon: ShieldCheck,  label: 'Confirmed' },
  processing: { color: '#9945FF', icon: Package,      label: 'Processing' },
  shipped:    { color: '#FF6B00', icon: Truck,        label: 'Shipped' },
  delivered:  { color: '#00C864', icon: CheckCircle,  label: 'Delivered' },
  completed:  { color: '#00C864', icon: CheckCircle,  label: 'Completed' },
  cancelled:  { color: '#FF3232', icon: XCircle,      label: 'Cancelled' },
  disputed:   { color: '#FF3232', icon: AlertTriangle,label: 'Disputed' },
  refunded:   { color: '#6B6B8A', icon: XCircle,      label: 'Refunded' },
};

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('buying');
  const [actionLoading, setActionLoading] = useState('');

  useEffect(() => {
    fetchOrders();
  }, [view]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const endpoint = view === 'buying' ? '/orders/my-orders' : '/orders/my-sales';
      const res = await api.get(endpoint);
      setOrders(res.data.orders || []);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const confirmDelivery = async (orderId) => {
    setActionLoading(orderId + '_confirm');
    try {
      await api.post(`/orders/${orderId}/confirm-delivery`);
      toast.success('Delivery confirmed! Funds released to seller.');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setActionLoading(''); }
  };

  const raiseDispute = async (orderId) => {
    const reason = prompt('Briefly describe the issue:');
    if (!reason) return;
    setActionLoading(orderId + '_dispute');
    try {
      await api.post(`/orders/${orderId}/dispute`, { reason });
      toast.success('Dispute raised. Admin will review within 24 hours.');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setActionLoading(''); }
  };

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mt-4 mb-6" style={{ fontFamily: 'Sora' }}>Orders</h1>

      {/* Tab */}
      {user?.role === 'seller' && (
        <div className="flex gap-2 mb-6 p-1 rounded-xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
          {['buying', 'selling'].map(t => (
            <button key={t} onClick={() => setView(t)}
              className="flex-1 py-2 rounded-lg text-sm font-semibold capitalize transition-all"
              style={{
                background: view === t ? 'linear-gradient(135deg,#FF6B00,#F5A623)' : 'transparent',
                color: view === t ? 'white' : '#6B6B8A',
              }}>
              {t === 'buying' ? '🛒 My Purchases' : '🏪 My Sales'}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-5xl mb-4">📦</div>
          <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Sora' }}>No orders yet</h3>
          <p className="text-brand-muted">Your orders will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => {
            const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const Icon = cfg.icon;
            return (
              <div key={order._id} className="rounded-2xl overflow-hidden" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #1E1E2E' }}>
                  <div>
                    <span className="text-xs text-brand-muted font-mono">#{order.orderNumber}</span>
                    <span className="text-xs text-brand-muted ml-3">{formatDistanceToNow(new Date(order.createdAt), { addSuffix: true })}</span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                    style={{ background: `${cfg.color}15`, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
                    <Icon size={11} /> {cfg.label}
                  </div>
                </div>

                {/* Items */}
                <div className="px-4 py-3">
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 mb-2">
                      <img src={item.imageUrl || 'https://via.placeholder.com/48/12121A/FF6B00?text=CM'}
                        alt={item.title} className="w-12 h-12 rounded-xl object-cover" style={{ border: '1px solid #1E1E2E' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.title}</p>
                        <p className="text-xs text-brand-muted">Qty: {item.quantity} × ${item.priceUSD?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Payment & Escrow info */}
                <div className="px-4 pb-3 grid grid-cols-2 gap-3">
                  <div className="p-2 rounded-xl" style={{ background: '#0A0A0F', border: '1px solid #1E1E2E' }}>
                    <p className="text-xs text-brand-muted">Paid</p>
                    <p className="text-sm font-bold font-mono" style={{ color: '#F7931A' }}>
                      {order.cryptoAmount?.toFixed(6)} {order.paymentCurrency}
                    </p>
                  </div>
                  <div className="p-2 rounded-xl" style={{ background: '#0A0A0F', border: '1px solid #1E1E2E' }}>
                    <p className="text-xs text-brand-muted">Escrow</p>
                    <p className="text-xs font-semibold" style={{ color: order.escrow?.status === 'released' ? '#00C864' : '#F5A623' }}>
                      {order.escrow?.status?.replace('_', ' ').toUpperCase() || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Auto-release countdown */}
                {order.escrow?.status === 'funded' && order.autoReleaseAt && (
                  <div className="px-4 pb-2">
                    <div className="flex items-center gap-2 p-2 rounded-lg text-xs" style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.2)' }}>
                      <Clock size={12} className="text-yellow-400" />
                      <span className="text-yellow-300">Auto-release: {formatDistanceToNow(new Date(order.autoReleaseAt), { addSuffix: true })}</span>
                    </div>
                  </div>
                )}

                {/* Delivery estimate */}
                {order.deliveryEstimate && (
                  <div className="px-4 pb-2">
                    <p className="text-xs text-brand-muted flex items-center gap-1">
                      <Truck size={11} /> Est. delivery: {order.deliveryEstimate.minDays}–{order.deliveryEstimate.maxDays} days
                      {order.deliveryEstimate.distanceKm && ` (~${order.deliveryEstimate.distanceKm}km)`}
                    </p>
                  </div>
                )}

                {/* Actions (buyer) */}
                {view === 'buying' && order.escrow?.status === 'funded' && !order.buyerConfirmedDelivery && (
                  <div className="px-4 pb-4 flex gap-2">
                    <button onClick={() => confirmDelivery(order._id)}
                      disabled={actionLoading === order._id + '_confirm'}
                      className="btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-1">
                      {actionLoading === order._id + '_confirm' ? <div className="spinner" /> : <><CheckCircle size={13} /> Item Received</>}
                    </button>
                    <button onClick={() => raiseDispute(order._id)}
                      disabled={actionLoading === order._id + '_dispute'}
                      className="flex-1 py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-1"
                      style={{ background: 'rgba(255,50,50,0.1)', color: '#FF3232', border: '1px solid rgba(255,50,50,0.3)' }}>
                      <AlertTriangle size={13} /> Dispute
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
